import cx from "classnames"
import { MediaRole, MediaType, SupplierCard_SupplierProductFragment } from "../../../../codegen/graphql"
import dayjs from "dayjs"
import { useCallback, useMemo, useState } from "react"
import Button from "../../common/Button"
import { DropdownMenu } from "../../common/DropdownMenu"
import { useImageTools } from "../../ImageTools/ImageProvider"
import { VariationMatchingModal } from "../VariationMatchingModal"
import { ImportVariationsModal } from "./SupplierCard_ImportVariationsModal"
import { FaStore } from "react-icons/fa"
import { EditNote_SupplierProduct_Modal } from "../EditNote_SupplierProduct_Modal"
import { DeliveryDestinations } from "../../../../common/deliveryDestinations"
import { getTranslatedValue } from "../../../../common/getTranslatedValue"
import { SupplierCard_importAliexpressImages } from "./SupplierCard_importAliexpressImages"
import { SupplierCard_importVideo } from "./SupplierCard_importAliexpressVideo"
import { SupplierCard_importAliexpressText } from "./SupplierCard_importAliexpressText"
import SupplierCard_VariationBlock from "./SupplierCard_VariationBlock"
import { useModalRouter } from "../../../ModalRouter_context"
import { getAliexpressProductQuickData } from "../../../../common/aliexpress/getAliexpressQuickData"
import { SupplierCard_SyncAliexpressSupplier, SupplierCard_SupplierProduct } from "./SupplierCard_SyncAliexpressSupplier"

export { SupplierCard_SupplierProduct }


export function SupplierCard({
  adId,
  supplierProduct,
  
  selectable = false,
  selectedVariations = [],

  updateAd,
  onVariationToggle,
}: { 
  adId: string, 
  supplierProduct: SupplierCard_SupplierProductFragment, 
  selectable?: boolean,
  selectedVariations?: Array<{
    sku: string;
    supplierProductId: string;
  }>,
  updateAd: (data: any) => void,
  onVariationToggle?: (variation: {
    sku: string;
    supplierProductId: string;
  }) => void
}) {
  const { openImageSelector } = useImageTools();
  const modalRouter = useModalRouter();
  
  // UI
  const [isFolded, setIsFolded] = useState(true);
  const [isVariationMatchingModalOpen, setIsVariationMatchingModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isImportVariationsModalOpen, setIsImportVariationsModalOpen] = useState(false);
  const [isSyncAliexpressModalOpen, setIsSyncAliexpressModalOpen] = useState(false);

  // State
  const [typeValuesFilter, setTypeValuesFilter] = useState<string[]>([])
  const [showForDestCountry, setShowForDestCountry] = useState<keyof typeof DeliveryDestinations>('FR')


  // Data
  const aliexpressCountryData = supplierProduct.data[showForDestCountry]
  let aliexpressQuickData = null

  /*
   * Event handlers
   */
  const toggleValueFilter = useCallback((value: string) => {
    setTypeValuesFilter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value])
  }, [])

  // Opens Aliexpress product page
  const handleOpenAliexpressProduct = useCallback((productId: string) => {
    window.open(`https://aliexpress.com/item/${productId}.html`, '_blank');
  }, []);

  // Opens the image selector for AliExpress main images or description images
  const openImportAliexpressImagesSelector = useCallback((kind: 'main' | 'description') => {
    let imageUrls: string[]
    let roles: MediaRole[]

    if (kind === 'main') {
      imageUrls = firstValid_supplierProductData.ae_multimedia_info_dto.image_urls.split(';')
      roles = [MediaRole.Main]
    } else {
      const IMG_HTML_REGEX = /<img[^>]*src="([^"]+)"[^>]*>/g;
      imageUrls = Array.from(
        firstValid_supplierProductData.ae_item_base_info_dto.detail.matchAll(IMG_HTML_REGEX),
        (match: any) => match[1]
      )
      roles = [MediaRole.Description]
    }

    openImageSelector({
      images: Array.from(new Set(imageUrls)).map((img: string) => ({
        id: img,
        url: img,
        type: MediaType.Image,
      })),
      onSelectionComplete: (images: any[]) => SupplierCard_importAliexpressImages(adId, images, roles),
    })
  }, [adId, supplierProduct.data, openImageSelector])
  
  const firstValid_supplierProductData = (
    (Object.entries(supplierProduct.data).find(([_country, obj]: any) => obj.success) as any)?.[1].data
  )


  // Dropdown actions
  const dropdownActions = useMemo(() => {
    const actions = []

    const isAE = supplierProduct.supplierName === 'aliexpress'

    const firstActionSubActions = [{
      label: 'Importer les variations',
      onClick: () => setIsImportVariationsModalOpen(true),
    }];

    
    if (isAE) {
      firstActionSubActions.push({
          label: 'Aliexpress: Importer les images principales',
          onClick: () => openImportAliexpressImagesSelector('main'),
        },
        {
          label: 'Aliexpress: Importer les images de description',
          onClick: () => openImportAliexpressImagesSelector('description'),
        },
        {
          label: 'Aliexpress: Importer la video',
          onClick: () => SupplierCard_importVideo(adId, firstValid_supplierProductData),
        },
        {
          label: 'Aliexpress: Importer le titre & description',
          onClick: () => SupplierCard_importAliexpressText(adId, supplierProduct.data, updateAd),
        }
      )
    }
    actions.push({
      label: 'Importer',
      subActions: firstActionSubActions,
    });

    actions.push({
      label: 'Match variations',
      onClick: () => setIsVariationMatchingModalOpen(true),
    });

    if (isAE) {
      actions.push({
        label: 'Synchroniser Aliexpress',
        onClick: () => setIsSyncAliexpressModalOpen(true),
      });
    }

    actions.push({
      label: 'Ajouter une note',
      onClick: () => setIsNoteModalOpen(true),
    });

    if (supplierProduct.supplierName === 'FR_ilyes') {
      actions.push({
        label: 'Manage inventory',
        onClick: () => {
          modalRouter.push({
            s: 'supplierProduct',
            a: {
              supplierProductId: supplierProduct.id,
            },
          })
        },
      });
    }

    return actions;
  }, [adId, supplierProduct.data, firstValid_supplierProductData, updateAd, openImportAliexpressImagesSelector])

  // Extract variations from product data
  const variations = supplierProduct.variations || [];

  // Check if a variation is selected
  const isVariationSelected = (variation: any) => {
    if (!selectable || !selectedVariations) return false;

    return selectedVariations.some(
      v => v.sku === variation.sku && 
           v.supplierProductId === variation.supplierProductId
    );
  };

  // Handle variation click when in selectable mode
  const handleVariationClick = (variation: any) => {
    if (selectable && onVariationToggle) {
      onVariationToggle({
        sku: variation.sku,
        supplierProductId: variation.supplierProductId
      });
    }
  };

  // Calculate summary data
  const summaryData = useMemo(() => {
    const propertyMap = new Map<string, {id: string, name: string, values: Set<string>}>();
    let minPrice = Infinity;
    let maxPrice = 0;
    let linkedVariations = 0;
    let unlinkedVariations = 0;
    let totalQuantityOrdered = 0;
    let totalAvailableStock = 0;
    let currency = '';

    variations.forEach((variation) => {
      // Update price range
      // TODO: use calculateAllPricesForAllPlatformsAndDestinations
      const calculatedCost = variation.calculatedCost.find((cc) => cc.destinationCountry === showForDestCountry);
      const price = calculatedCost?.totalCostIncludingVAT ?? 0;
      minPrice = Math.min(minPrice, price);
      maxPrice = Math.max(maxPrice, price);
      currency = calculatedCost?.currency ?? '';

      // Track linked/unlinked variations
      if (variation.adVariations && variation.adVariations.length > 0) {
        linkedVariations++;
      } else {
        unlinkedVariations++;
      }

      // Add to total quantity ordered
      totalQuantityOrdered += variation.quantityOrdered || 0;

      // Add to total available stock
      totalAvailableStock += variation.availableStock?.find((stock) => (
        stock.destinationCountry === showForDestCountry
      ))?.availableStock || 0;

      // Collect property values
      if (variation.typeValues) {
        variation.typeValues.forEach((attr) => {
          if (!propertyMap.has(attr.type.id)) {
            propertyMap.set(attr.type.id, {id: attr.type.id, name: getTranslatedValue(attr.type.type, showForDestCountry), values: new Set()});
          }
          propertyMap.get(attr.type.id)!.values.add(getTranslatedValue(attr.value, showForDestCountry));
        });
      }
    });

    return {
      properties: Array.from(propertyMap.values()),
      priceRange: {
        min: minPrice === Infinity ? 0 : minPrice,
        max: maxPrice
      },
      linkedVariations,
      unlinkedVariations,
      totalQuantityOrdered,
      totalAvailableStock,
      currency
    };
  }, [variations, showForDestCountry]);


  let storeRatings = null
  let storeName = null
  if (aliexpressCountryData && aliexpressCountryData.success) {
    aliexpressQuickData = getAliexpressProductQuickData(aliexpressCountryData)
    const storeRatingShippingSpeed = aliexpressQuickData.store.shippingSpeedRating
    const storeRatingCommunication = aliexpressQuickData.store.communicationRating
    const storeRatingItemAsDescribed = aliexpressQuickData.store.itemAsDescribedRating
    const storeAverageRating = (storeRatingShippingSpeed + storeRatingCommunication + storeRatingItemAsDescribed) / 3
    storeRatings = {
      storeAverageRating,
      storeRatingShippingSpeed,
      storeRatingCommunication,
      storeRatingItemAsDescribed,
    }
    storeName = aliexpressQuickData.store.name
  }
  else if (supplierProduct.supplierName === 'FR_ilyes') {
    storeName = 'Inventaire local'
  }

  return (
    <div className="bg-white shadow-sm rounded-lg p-6 mb-4">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          {/* Note display */}
          {(supplierProduct.note) && (
            <div 
              className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded-lg cursor-pointer hover:bg-yellow-100"
              onClick={() => setIsNoteModalOpen(true)}
            >
              {supplierProduct.note}
            </div>
          )}


          <div className="flex flex-row items-center gap-2 mb-2 text-lg">
            <FaStore
              className={cx(
                supplierProduct.supplierName === 'aliexpress' && "text-yellow-600",
                supplierProduct.supplierName === 'FR_ilyes' && "text-gray-500",
              )}
              size={20}
            />
            <span className={cx(
              'font-bold',
              supplierProduct.supplierName === 'aliexpress' && "text-red-600",
              supplierProduct.supplierName === 'FR_ilyes' && "text-gray-500",
            )}>
              {storeName}
            </span>
            {storeRatings && (
              <span className="text-yellow-600 text-base font-bold border border-yellow-600 rounded-md px-1 py-0.5 line-height-1">
                {storeRatings.storeAverageRating.toFixed(1)}
              </span>
            )}
          </div>
          
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setIsFolded(!isFolded)}
          >
            <span className="text-gray-500 text-xl">
              {isFolded ? '▼' : '▲'}
            </span>
            <h2 className="text-2xl font-semibold">
              {aliexpressQuickData && (
                <span className="text-yellow-600 font-bold text-base mr-1 border border-yellow-600 rounded-md px-1 py-1">
                  {aliexpressQuickData.store.rating}
                </span>
              )}
              {supplierProduct.externalProductId && `${supplierProduct.externalProductId} - `}
              {getTranslatedValue(supplierProduct.name, showForDestCountry)}
            </h2>
          </div>
          {supplierProduct.supplierName === 'aliexpress' && (
            <p className="text-lg text-gray-500">
              Dernière synchronisation: {dayjs(supplierProduct.lastSyncAt).format('DD/MM/YYYY à HH:mm')}
            </p>
          )}
          
          {/* Summary section */}
          <div className="mt-4 text-base text-gray-600">
            <div className="mb-3">
              <span className="font-medium text-lg">Variations:</span>{' '}
              <span className="text-lg">{summaryData.linkedVariations} liées, {summaryData.unlinkedVariations} non liées</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {summaryData.properties.map((prop, index) => (
                <div key={index} className="flex flex-wrap gap-1">
                  <div className={cx("bg-gray-100 px-3 py-1 rounded-full text-sm font-medium")}>
                    {prop.name}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {Array.from(prop.values).map((value, valueIndex) => (
                      <div
                        key={valueIndex}
                        className={cx(
                          "bg-blue-50 px-3 py-1 rounded-full text-sm cursor-pointer",
                          typeValuesFilter.includes(value) && "bg-blue-500 text-white",
                        )}
                        onClick={() => toggleValueFilter(value)}
                      >
                        {value}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-4 ml-4">
          <div className="text-right">
            <div className="text-2xl font-medium text-gray-900">
              {summaryData.priceRange.min === summaryData.priceRange.max
                ? summaryData.priceRange.min.toFixed(2)
                : `${summaryData.priceRange.min.toFixed(2)} - ${summaryData.priceRange.max.toFixed(2)}`}
            </div>
            <div className="text-base text-gray-500">
              {summaryData.currency}
            </div>
            <div className="text-lg font-medium text-blue-600 mt-1">
              {summaryData.totalQuantityOrdered} commandés
            </div>
            <div className="text-lg font-medium text-gray-800 mt-1">
              {summaryData.totalAvailableStock > 0 ? `${summaryData.totalAvailableStock} stock` : 'Rupture de stock'}
            </div>
            {supplierProduct.supplierName === 'aliexpress' && (
              <DropdownMenu
                label={DeliveryDestinations[showForDestCountry].flag}
                actions={Object.keys(DeliveryDestinations)
                  .map((destCountry) => ({
                    label: (
                      `${destCountry in supplierProduct.data
                        ? dayjs(supplierProduct.data[destCountry].lastSyncAt).format('DD/MM/YYYY à HH:mm')
                        : '(no data)'} - ${DeliveryDestinations[destCountry].name}`
                    ),
                    onClick: () => setShowForDestCountry(destCountry),
                  }))}
                buttonClassName="text-base"
              />
            )}
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={() => handleOpenAliexpressProduct(aliexpressQuickData?.externalProductId ?? '')}
              className="bg-white text-gray-800 border border-gray-300 text-base py-2 px-4"
            >
              Ouvrir le produit
            </Button>
            <DropdownMenu
              label="Actions"
              actions={dropdownActions}
              buttonClassName="text-base"
            />
          </div>
        </div>
      </div>
      
      {/* Variations list */}
      {!isFolded && variations.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {variations.map((variation) => (
            <SupplierCard_VariationBlock
              key={variation.sku}
              variation={variation}
              selectable={selectable}
              isVariationSelected={isVariationSelected}
              handleVariationClick={handleVariationClick}
              valuesFilter={typeValuesFilter}
              destCountry={showForDestCountry}
            />
          ))}
        </div>
      )}

      {/* Variation Matching Modal */}
      {isVariationMatchingModalOpen && (
        <VariationMatchingModal
          onClose={() => setIsVariationMatchingModalOpen(false)}
          adId={adId}
          supplierProductId={supplierProduct.id}
        />
      )}

      {/* Import Variations Modal */}
      {isImportVariationsModalOpen && (
        <ImportVariationsModal
          adId={adId}
          onClose={() => setIsImportVariationsModalOpen(false)}
          product={supplierProduct}
        />
      )}

      {/* Note Edit Modal */}
      {isNoteModalOpen && (
        <EditNote_SupplierProduct_Modal
          onClose={() => setIsNoteModalOpen(false)}
          supplierProduct={supplierProduct}
        />
      )}

      {isSyncAliexpressModalOpen && supplierProduct.externalProductId && (
        <SupplierCard_SyncAliexpressSupplier
          productId={supplierProduct.externalProductId}
          data={supplierProduct.data}
          initialCountry={showForDestCountry}
          onClose={() => setIsSyncAliexpressModalOpen(false)}
        />
      )}

    </div>
  );
}